import Observer from '@researchgate/react-intersection-observer';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityNotificationsStore, getGlobalActivityNotificationsStore, setGlobalActivityNotificationsStore } from '@udemy/activity-notifications';
import { Tracker } from '@udemy/event-tracking';
import { useMatchMedia } from '@udemy/hooks';
import { useI18n } from '@udemy/i18n';
import { Icon } from '@udemy/icons';
import { CheckedStateCheckbox, isChecked } from '@udemy/react-checked-state-components';
import { IconButton } from '@udemy/react-core-components';
import { FocusTrappingDialog } from '@udemy/react-dialog-components';
import { BlockSkeleton } from '@udemy/react-reveal-components';
import { MWebOverlaySearchFormAutocomplete, MWebOverlaySearchStyles, SearchFormAutocomplete, SearchFormAutocompleteStore } from '@udemy/search-form-autocomplete';
import { SmartBarFixedHeight, SmartBarFixedHeightSpacer } from '@udemy/smart-bar';
import { useUDData } from '@udemy/ud-data';
import { CartBadge } from '../badges.react-component';
import { HeaderContextProvider } from '../contexts/header-context';
import { SearchBarMXEnum, SearchBarSelected } from '../events';
import { showCobrandingLogo, isSearchInputAutoFocusEnabled, isOptimizedSearchHeaderMWebEnabled } from '../external/header-experiment-features';
import { loadCommonAppContext } from '../external/load-common-app-context';
import { HeaderStore } from '../header.mobx-store';
import { Logo } from '../logo.react-component';
import { SeoLinks } from '../seo-links/seo-links.react-component';
import globalHeaderStyles from "./mobile-header-global.module.css";
import styles from "./mobile-header.module.css";
import { MobileIAStudentNav } from './mobile-nav/mobile-ia-student-nav.react-component';
import { MobileNavStore } from './mobile-nav/mobile-nav.mobx-store';
import twoRowSearchStyles from "./mobile-two-row-header.module.css";
export const MobileHeader = observer(({
  searchPhrase = '',
  ufbContext,
  isInsideDesktopHeader = false,
  persistentSearch = false,
  shoppingClient,
  useLangPrefixedUrls = false,
  mobileAppLink,
  showSmartBar = true,
  headerExperimentFeatures,
  headerData,
  showTwoRowSearchBarMX
}) => {
  var _udData$Config, _udData$Config2, _searchFormAutocomple;
  const effectiveHeaderExperimentFeatures = headerExperimentFeatures !== null && headerExperimentFeatures !== void 0 ? headerExperimentFeatures : headerData === null || headerData === void 0 ? void 0 : headerData.headerExperimentFeatures;
  const isUFB = !!ufbContext;
  const udData = useUDData();
  const udConfig = udData.Config;
  const {
    gettext,
    locale,
    lang,
    ...rest
  } = useI18n();
  const [mobileNavStore] = useState(() => new MobileNavStore(lang, gettext));
  const [wasSearchPhraseInitialized, setWasSearchPhraseInitialized] = useState(false);
  const [headerStore] = useState(() => new HeaderStore({
    mobileAppLink,
    ufbContext,
    shoppingClient,
    headerExperimentFeatures: effectiveHeaderExperimentFeatures
  }));
  const useGlobalHeaderBar = isOptimizedSearchHeaderMWebEnabled({
    headerExperimentFeatures: effectiveHeaderExperimentFeatures,
    headerStore
  });
  const isUBUser = udData.me.is_authenticated && !!udData.me.organization;
  const isPPUser = headerStore.isPersonalPlanSubscriber;
  const isMobileXsMax = useMatchMedia('mobile-xs-max');
  const [searchFormAutocompleteStore] = useState(() => new SearchFormAutocompleteStore({
    getFormParams: () => headerStore.formActionParams,
    gettext,
    inputValue: searchPhrase
  }));
  const ufbStores = useMemo(() => {
    var _ufbContext$createSto;
    return ufbContext === null || ufbContext === void 0 || (_ufbContext$createSto = ufbContext.createStores) === null || _ufbContext$createSto === void 0 ? void 0 : _ufbContext$createSto.call(ufbContext, headerStore);
  }, [headerStore, ufbContext]);
  const isAggregatorFeatureEnabled = headerStore.isAggregatorFeatureEnabled;
  const isSearchInputAutoFocusExperimentEnabled = isSearchInputAutoFocusEnabled({
    headerExperimentFeatures: effectiveHeaderExperimentFeatures,
    headerStore
  });
  const isLoadedTwoRowSearchExp = showTwoRowSearchBarMX !== SearchBarMXEnum.NotLoaded;
  const showTwoRowSearchBar = showTwoRowSearchBarMX === SearchBarMXEnum.NewBar;
  const searchDialogRef = useRef();
  const searchInputRef = useRef(null);
  // TODO: do we need this?
  // const searchBarCheckboxRef = useRef<any>();
  const [floatingHeaderIsVisible, setFloatingHeaderIsVisible] = useState(false);
  const lastScrollY = useRef(0);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [isSearchBarOpen, setIsSearchBarOpen] = useState(false);
  const isHeaderContextLoaded = Object.keys(headerStore.userSpecificContext).length > 0;
  const isExperimentReady = isHeaderContextLoaded && !!effectiveHeaderExperimentFeatures;
  useEffect(() => {
    if (ufbContext && !headerStore.ufbContext) {
      headerStore.setUfbContext(ufbContext);
    }
  }, [ufbContext, headerStore]);
  useEffect(() => {
    mobileNavStore.loadAiLearningNav();
    (async () => {
      const response = await loadCommonAppContext(locale, isUFB);
      // When headerExperimentFeatures is passed (e.g. from Storybook), merge it into
      // the context so the store's computed flags (e.g. isSplitNavByCategoriesEnabled) use it.
      const headerContext = effectiveHeaderExperimentFeatures != null ? {
        ...response.data.header,
        headerExperimentFeatures: effectiveHeaderExperimentFeatures
      } : response.data.header;
      headerStore.setUserSpecificContext(headerContext);
      const existingStore = getGlobalActivityNotificationsStore();
      if (!existingStore) {
        const activityNotificationsStore = new ActivityNotificationsStore({
          gettext,
          locale,
          lang,
          ...rest
        });
        setGlobalActivityNotificationsStore(activityNotificationsStore);
        activityNotificationsStore.setUserSpecificContext({
          user: headerStore.userSpecificContext.user,
          // If `nav_user_type = 'instructor'`, InstructorHeader is rendered.
          userType: null
        });
      }
      mobileNavStore.loadNavigationCategories(response.data.header);
      if (!headerStore.userSpecificContext.isLoggedIn) {
        mobileNavStore.ensureLevelIsLoaded(1);
        mobileNavStore.loadMostPopularTopicsForSubcategories(locale);
      }
      // Fetch subscription info for non-organization users
      // This works for both logged-in and logged-out users
      if (!isUFB) {
        await headerStore.fetchSubscriptionInfo();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // Load aggregator topics if the user is not a PP or UB user and aggregator experiment is enabled
    if (isAggregatorFeatureEnabled && !isPPUser && !isUBUser) {
      mobileNavStore.loadNavigationAggregatorTopics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAggregatorFeatureEnabled, isPPUser, isUBUser]);

  // Track scroll direction so the global header bar only appears on scroll-up
  useEffect(() => {
    if (!useGlobalHeaderBar) {
      return;
    }
    const SCROLL_THRESHOLD = 5;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const diff = lastScrollY.current - currentScrollY;
      if (Math.abs(diff) >= SCROLL_THRESHOLD) {
        setIsScrollingUp(diff > 0);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [useGlobalHeaderBar]);
  useEffect(() => {
    if (!useGlobalHeaderBar) {
      return;
    }
    if (isSearchBarOpen) {
      var _searchDialogRef$curr;
      (_searchDialogRef$curr = searchDialogRef.current) === null || _searchDialogRef$curr === void 0 || _searchDialogRef$curr.onToggle(true);
      if (searchInputRef.current) {
        var _searchInputRef$curre;
        const length = ((_searchInputRef$curre = searchInputRef.current.value) === null || _searchInputRef$curre === void 0 ? void 0 : _searchInputRef$curre.length) || 0;
        searchInputRef.current.focus();
        searchInputRef.current.setSelectionRange(length, length); // Set cursor at the end
      }
    } else {
      var _searchDialogRef$curr2;
      (_searchDialogRef$curr2 = searchDialogRef.current) === null || _searchDialogRef$curr2 === void 0 || _searchDialogRef$curr2.onToggle(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchBarOpen]);
  function handleHeaderIntersection(entry) {
    const intersectionRatio = entry.intersectionRatio;

    // Display floating header if normal header is entirely out of view
    if (!floatingHeaderIsVisible && intersectionRatio === 0) {
      setFloatingHeaderIsVisible(true);
    }
  }
  function handleTopMarkIntersection(entry) {
    const {
      top
    } = entry.boundingClientRect;

    // However, only restore normal header if we are all the way back at the top of the page.
    if (floatingHeaderIsVisible && top >= 0) {
      setFloatingHeaderIsVisible(false);
    }
  }
  function findSearchBarNodeToFocusOn() {
    return document.getElementById('header-mobile-search-bar');
  }
  function trackSeachBarSelected() {
    Tracker.publishEvent(new SearchBarSelected());
  }
  function onChangeSearchBar(event) {
    var _searchDialogRef$curr3;
    const isSearchBarStateChecked = isChecked(event);
    (_searchDialogRef$curr3 = searchDialogRef.current) === null || _searchDialogRef$curr3 === void 0 || _searchDialogRef$curr3.onToggle(isSearchBarStateChecked);
    if (searchPhrase && !wasSearchPhraseInitialized && isSearchBarStateChecked) {
      // Initialize the search phrase the first time the search bar is opened.
      // We need to do this here, after the search bar receives focus,
      // so that the cursor is positioned at the end of the input value.
      searchFormAutocompleteStore.setInputValue(searchPhrase || '');
      setWasSearchPhraseInitialized(true);
    }
    if (isSearchBarStateChecked) {
      searchFormAutocompleteStore.openMenu();
    } else {
      searchFormAutocompleteStore.closeMenu();
    }
    if (searchInputRef.current) {
      var _searchInputRef$curre2;
      const length = ((_searchInputRef$curre2 = searchInputRef.current.value) === null || _searchInputRef$curre2 === void 0 ? void 0 : _searchInputRef$curre2.length) || 0;
      searchInputRef.current.focus();
      searchInputRef.current.setSelectionRange(length, length); // Set cursor at the end
    }
  }
  function getSearchBarLabel() {
    var _headerStore$userSpec;
    if (isUFB && (_headerStore$userSpec = headerStore.userSpecificContext) !== null && _headerStore$userSpec !== void 0 && (_headerStore$userSpec = _headerStore$userSpec.user) !== null && _headerStore$userSpec !== void 0 && _headerStore$userSpec.enable_in_lecture_search_segments) {
      return gettext('What would you like to learn today?');
    }
    return (effectiveHeaderExperimentFeatures === null || effectiveHeaderExperimentFeatures === void 0 ? void 0 : effectiveHeaderExperimentFeatures.autocompleteLabel) || gettext('Search for anything');
  }
  function renderTwoRowHeaderBar(udConfig, floating) {
    const showCobrandLogo = showCobrandingLogo({
      headerExperimentFeatures: effectiveHeaderExperimentFeatures,
      headerStore
    });
    return /*#__PURE__*/React.createElement("header", {
      className: classNames('ud-header', styles.header, styles['with-shadow'], twoRowSearchStyles['multi-header'], {
        [styles.floating]: floating
      }),
      "data-testid": "mobile-header-new"
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.row)
    }, /*#__PURE__*/React.createElement(IconButton, {
      udStyle: "ghost",
      cssToggleId: "header-toggle-side-nav",
      className: "ud-mobile-header-btn",
      "data-purpose": "side-menu-opener"
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "menu",
      color: "neutral",
      label: gettext('Open side drawer')
    })), udConfig.features.shopping_cart && headerStore.showCartDropdown && (!showCobrandLogo || showCobrandLogo && isMobileXsMax) && !showTwoRowSearchBar && /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['button-spacer'])
    }), /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.row, styles.middle)
    }, /*#__PURE__*/React.createElement("a", {
      href: headerStore.urls.BROWSE
    }, /*#__PURE__*/React.createElement(Logo, {
      ufbContext: ufbContext,
      width: showCobrandLogo ? 120 : 75,
      height: 28,
      style: {
        verticalAlign: 'middle'
      },
      showCobrandingLogo: showCobrandLogo
    }))), udConfig.features.shopping_cart && headerStore.showCartDropdown && /*#__PURE__*/React.createElement(IconButton, {
      udStyle: "ghost",
      componentClass: "a",
      href: headerStore.urls.CART,
      overlaychildren: /*#__PURE__*/React.createElement(CartBadge, {
        className: classNames(styles['cart-badge'])
      }),
      className: "ud-mobile-header-btn"
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "cart",
      color: "neutral",
      label: gettext('Go to cart')
    }))), /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.row)
    }, isLoadedTwoRowSearchExp ? /*#__PURE__*/React.createElement(SearchFormAutocomplete, {
      submitButtonProps: {
        udStyle: 'primary',
        round: true,
        size: 'large'
      },
      searchFormAutocompleteStore: searchFormAutocompleteStore,
      id: "header-mobile-search-bar-multi",
      formAction: headerStore.urls.SEARCH,
      formActionParams: headerStore.formActionParams,
      label: getSearchBarLabel(),
      inputProps: {
        className: 'js-header-search-field-multi',
        name: 'q'
      }
      // reversed={true}
      ,
      textSize: "small",
      formClassName: 'form-search-bar',
      className: classNames(twoRowSearchStyles['search-bar-multi']),
      showResultsWithImage: true
    }) : /*#__PURE__*/React.createElement(React.Fragment, null)));
  }
  function openSearchBar() {
    setIsSearchBarOpen(true);
    if (searchPhrase && !wasSearchPhraseInitialized) {
      searchFormAutocompleteStore.setInputValue(searchPhrase || '');
      setWasSearchPhraseInitialized(true);
    }
    searchFormAutocompleteStore.openMenu();
  }
  function closeSearchBar() {
    setIsSearchBarOpen(false);
    searchFormAutocompleteStore.closeMenu();
  }
  function renderHeaderBarGlobal(udConfig, floating, hidden = false) {
    return /*#__PURE__*/React.createElement("header", {
      className: classNames('ud-header', globalHeaderStyles['global-header'], styles['with-shadow'], {
        [globalHeaderStyles['global-header-floating']]: floating,
        [globalHeaderStyles['global-header-floating-hidden']]: hidden
      }),
      "data-testid": "mobile-header-global"
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(globalHeaderStyles['global-row'])
    }, /*#__PURE__*/React.createElement(IconButton, {
      udStyle: "ghost",
      cssToggleId: "header-toggle-side-nav",
      className: "ud-mobile-header-btn",
      "data-purpose": "side-menu-opener"
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "menu",
      color: "neutral",
      label: gettext('Open side drawer')
    })), /*#__PURE__*/React.createElement(IconButton, {
      udStyle: "ghost",
      className: "ud-mobile-header-btn ud-link-neutral",
      componentClass: "a",
      href: "/"
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "udemy-symbol",
      label: gettext('Go to Udemy home page'),
      size: "medium"
    })), /*#__PURE__*/React.createElement(SearchFormAutocomplete, {
      submitButtonProps: {
        udStyle: 'ghost',
        round: true,
        size: 'small',
        className: globalHeaderStyles['global-header-search-button'],
        onClick: openSearchBar,
        iconColor: 'subdued'
      },
      searchFormAutocompleteStore: searchFormAutocompleteStore,
      id: "header-mobile-search-bar-global",
      formAction: headerStore.urls.SEARCH,
      formActionParams: headerStore.formActionParams,
      label: getSearchBarLabel(),
      inputProps: {
        className: globalHeaderStyles['search-field-global'],
        name: 'search-input-global',
        onClick: openSearchBar,
        readOnly: true
      },
      reversed: true,
      textSize: "small",
      formClassName: '',
      className: classNames(globalHeaderStyles['search-bar-global']),
      showResultsWithImage: true,
      disableMenu: true
    }), udConfig.features.shopping_cart && headerStore.showCartDropdown && /*#__PURE__*/React.createElement(IconButton, {
      udStyle: "ghost",
      componentClass: "a",
      href: headerStore.urls.CART,
      overlaychildren: /*#__PURE__*/React.createElement(CartBadge, {
        className: classNames(globalHeaderStyles['cart-badge'])
      }),
      className: "ud-mobile-header-btn"
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "cart",
      color: "neutral",
      label: gettext('Go to cart')
    }))));
  }
  function renderHeaderBar(udConfig, floating) {
    const showCobrandLogo = showCobrandingLogo({
      headerExperimentFeatures: effectiveHeaderExperimentFeatures,
      headerStore
    });
    return /*#__PURE__*/React.createElement("header", {
      className: classNames('ud-header', styles.header, styles['with-shadow'], {
        [styles.floating]: floating
      }),
      "data-testid": "mobile-header"
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.row)
    }, /*#__PURE__*/React.createElement(IconButton, {
      udStyle: "ghost",
      cssToggleId: "header-toggle-side-nav",
      className: "ud-mobile-header-btn",
      "data-purpose": "side-menu-opener"
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "menu",
      color: "neutral",
      label: gettext('Open side drawer')
    })), udConfig.features.shopping_cart && headerStore.showCartDropdown && (!showCobrandLogo || showCobrandLogo && isMobileXsMax) && /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['button-spacer'])
    }), /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.row, styles.middle)
    }, /*#__PURE__*/React.createElement("a", {
      href: headerStore.urls.BROWSE
    }, /*#__PURE__*/React.createElement(Logo, {
      ufbContext: ufbContext,
      width: showCobrandLogo ? 120 : 75,
      height: 28,
      style: {
        verticalAlign: 'middle'
      },
      showCobrandingLogo: showCobrandLogo
    }))), /*#__PURE__*/React.createElement(IconButton, {
      udStyle: "ghost",
      cssToggleId: "header-toggle-search-bar",
      className: "ud-mobile-header-btn"
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "search",
      id: "mobile-header-open-search-icon",
      color: "neutral",
      label: gettext('Open search')
    })), udConfig.features.shopping_cart && headerStore.showCartDropdown && /*#__PURE__*/React.createElement(IconButton, {
      udStyle: "ghost",
      componentClass: "a",
      href: headerStore.urls.CART,
      overlaychildren: /*#__PURE__*/React.createElement(CartBadge, {
        className: classNames(styles['cart-badge'])
      }),
      className: "ud-mobile-header-btn"
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "cart",
      color: "neutral",
      label: gettext('Go to cart')
    }))));
  }

  /** Renders a skeleton that mirrors the global header layout while experiment bucketing is in progress. */
  function renderHeaderSkeleton() {
    return /*#__PURE__*/React.createElement("header", {
      className: classNames('ud-header', globalHeaderStyles['global-header']),
      "data-testid": "mobile-header-skeleton"
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(globalHeaderStyles['global-row'])
    }, /*#__PURE__*/React.createElement(BlockSkeleton, {
      className: styles['skeleton-btn'],
      parentSkeletonProps: {
        className: styles['skeleton-btn-parent']
      }
    }), /*#__PURE__*/React.createElement(BlockSkeleton, {
      className: styles['skeleton-btn'],
      parentSkeletonProps: {
        className: styles['skeleton-btn-parent']
      }
    }), /*#__PURE__*/React.createElement(BlockSkeleton, {
      className: styles['skeleton-search-pill'],
      parentSkeletonProps: {
        className: styles['skeleton-search-pill-parent']
      }
    }), /*#__PURE__*/React.createElement(BlockSkeleton, {
      className: styles['skeleton-btn'],
      parentSkeletonProps: {
        className: styles['skeleton-btn-parent']
      }
    })));
  }
  function renderHeader() {
    return floatingHeaderIsVisible && (useGlobalHeaderBar ? renderHeaderBarGlobal(udConfig, true, !isScrollingUp) : (persistentSearch || showTwoRowSearchBar) && (showTwoRowSearchBar ? renderTwoRowHeaderBar(udConfig, true) : renderHeaderBar(udConfig, true)));
  }
  const searchAutocompleteBaseProps = {
    searchFormAutocompleteStore,
    id: 'header-mobile-search-bar',
    formAction: headerStore.urls.SEARCH,
    formActionParams: headerStore.formActionParams,
    label: getSearchBarLabel(),
    inputProps: {
      name: 'q',
      onFocus: trackSeachBarSelected,
      className: 'js-header-search-field'
    },
    reversed: true,
    showResultsWithImage: true
  };
  const isUbContext = !!ufbContext && Object.keys(ufbContext).length > 0;
  const isTeamPlan = (_udData$Config = udData.Config) === null || _udData$Config === void 0 || (_udData$Config = _udData$Config.brand) === null || _udData$Config === void 0 ? void 0 : _udData$Config.is_team;

  // Check if smart bar is enabled via feature flag
  // This prevents unnecessary component mounting when disabled (e.g., on cart page)
  const isSmartBarEnabled = (_udData$Config2 = udData.Config) === null || _udData$Config2 === void 0 || (_udData$Config2 = _udData$Config2.features) === null || _udData$Config2 === void 0 || (_udData$Config2 = _udData$Config2.notice) === null || _udData$Config2 === void 0 ? void 0 : _udData$Config2.smart_bar;
  return /*#__PURE__*/React.createElement(HeaderContextProvider, Object.assign({
    headerStore: headerStore,
    mobileNavStore: mobileNavStore
  }, ufbStores, {
    ufbContext: ufbContext,
    headerExperimentFeatures: effectiveHeaderExperimentFeatures
  }), /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CheckedStateCheckbox, {
    id: "header-toggle-search-bar",
    className: "ud-full-page-overlay-checkbox",
    onChange: onChangeSearchBar,
    closeOnEscape: true
  }), !isInsideDesktopHeader && showSmartBar && isSmartBarEnabled && /*#__PURE__*/React.createElement(SmartBarFixedHeight, {
    isPersonalPlanSubscriber: headerStore.isPersonalPlanSubscriber,
    isUdemyBusinessSubscriber: isUbContext && !isTeamPlan,
    isEnterprisePlanSubscriber: isUbContext && !isTeamPlan,
    isTeamPlanSubscriber: isUbContext && isTeamPlan,
    isUserContextReady: !!headerStore.userSpecificContext.user
  }), !isInsideDesktopHeader && showSmartBar && isSmartBarEnabled && /*#__PURE__*/React.createElement(SmartBarFixedHeightSpacer, {
    disableHideButtonWhenVisible: true
  }), /*#__PURE__*/React.createElement(Observer, {
    onChange: handleTopMarkIntersection
  }, /*#__PURE__*/React.createElement("span", {
    className: classNames(styles.mark)
  })), /*#__PURE__*/React.createElement(Observer, {
    onChange: handleHeaderIntersection
  }, isExperimentReady ? (useGlobalHeaderBar ? renderHeaderBarGlobal : showTwoRowSearchBar ? renderTwoRowHeaderBar : renderHeaderBar)(udConfig, false) : renderHeaderSkeleton()), isExperimentReady && renderHeader(), /*#__PURE__*/React.createElement(FocusTrappingDialog, {
    ref: searchDialogRef,
    labelledById: "header-mobile-search-bar-title",
    findNodeToFocusOn: findSearchBarNodeToFocusOn,
    className: useGlobalHeaderBar ? classNames(globalHeaderStyles['search-overlay'], styles['search-bar-layer']) : classNames(styles['search-bar'], styles['search-bar-layer'])
    // Global header style overrides: drives overlay visibility via React state instead of CSS toggle (hide by default).
    ,
    style: useGlobalHeaderBar ? {
      display: isSearchBarOpen ? 'flex' : 'none'
    } : undefined
  }, useGlobalHeaderBar ? /*#__PURE__*/React.createElement(MWebOverlaySearchFormAutocomplete, {
    onClose: closeSearchBar,
    onClearInput: searchFormAutocompleteStore.clearInputValue,
    showClearButton: Boolean((_searchFormAutocomple = searchFormAutocompleteStore.inputValue) === null || _searchFormAutocomple === void 0 ? void 0 : _searchFormAutocomple.trim())
  }, /*#__PURE__*/React.createElement(SearchFormAutocomplete, Object.assign({}, searchAutocompleteBaseProps, {
    inputRef: searchInputRef,
    textSize: "small",
    className: MWebOverlaySearchStyles.autocomplete,
    formClassName: MWebOverlaySearchStyles.form,
    inputProps: {
      ...searchAutocompleteBaseProps.inputProps,
      className: classNames(searchAutocompleteBaseProps.inputProps.className, MWebOverlaySearchStyles.input)
    },
    submitButtonProps: {
      udStyle: 'ghost',
      className: MWebOverlaySearchStyles.submitButton
    },
    suggestionsClassName: MWebOverlaySearchStyles.suggestions
  }))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(IconButton, {
    udStyle: "ghost",
    cssToggleId: "header-toggle-search-bar",
    className: classNames('ud-mobile-header-btn', styles['search-bar-close'])
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "close",
    color: "neutral",
    label: gettext('Close search')
  })), /*#__PURE__*/React.createElement("span", {
    className: "ud-sr-only"
  }, gettext('Search bar')), isLoadedTwoRowSearchExp ? /*#__PURE__*/React.createElement(SearchFormAutocomplete, Object.assign({}, searchAutocompleteBaseProps, {
    inputRef: isSearchInputAutoFocusExperimentEnabled ? searchInputRef : undefined,
    className: classNames(styles['search-bar-form'])
  })) : /*#__PURE__*/React.createElement(React.Fragment, null))), /*#__PURE__*/React.createElement(MobileIAStudentNav, {
    useLangPrefixedUrls: useLangPrefixedUrls
  }), !headerStore.userSpecificContext.isLoggedIn && /*#__PURE__*/React.createElement("div", {
    "data-testid": "seo-links"
  }, /*#__PURE__*/React.createElement(SeoLinks, {
    navigationCategories: mobileNavStore.navigationCategories,
    mostPopularSubcategories: mobileNavStore.mostPopularSubcategories
  }))));
});
//# sourceMappingURL=mobile-header.react-component.js.map